import api from './api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission() {
  return typeof Notification !== 'undefined' ? Notification.permission : 'denied';
}

async function ready(timeoutMs = 8000) {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Service worker not ready')), timeoutMs)),
  ]);
}

async function subscribeAndSave(reg) {
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get('/push/vapid-public-key');
    if (!data.public_key) throw new Error('Push not configured on server');
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post('/push/subscribe', sub.toJSON());
  return sub;
}

export async function isPushEnabled() {
  if (!pushSupported() || pushPermission() !== 'granted') return false;
  try {
    const reg = await ready();
    return !!(await reg.pushManager.getSubscription());
  } catch {
    return false;
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error('Notifications are not supported on this browser');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission was not granted');
  const reg = await ready();
  await subscribeAndSave(reg);
  return true;
}

export async function disablePush() {
  if (!pushSupported()) return;
  try {
    const reg = await ready();
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await api.post('/push/unsubscribe', sub.toJSON()).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch { /* noop */ }
}

// Re-register the subscription silently if the user already granted permission.
export async function ensureSubscribedIfGranted() {
  if (!pushSupported() || pushPermission() !== 'granted') return;
  try {
    const reg = await ready();
    await subscribeAndSave(reg);
  } catch { /* noop */ }
}
